/**
 * Figma `Table`(40:2404) · `_Table/Row`(8:303) · `_Table/GridRow`(2088:17156) · `_Table/StackCell`(2058:3045) 에서 읽은 값.
 *
 * 표 4종(04-ui-system/index.md, DD-9 D1~D3):
 * - 목록 표 = `_Table/Row density=default` — 화면의 주된 목록(줄 54, 줄 사이 2px 틈)
 * - 작은 표 = `density=compact` — 카드 · 팝업 · 구획 안의 표(줄 43, 1px 회색 가로선)
 * - 낮은 표 = `density=dense` — 높이가 고정된 좁은 영역에서 스크롤되는 목록(줄 35)
 * - 격자 표 = `_Table/GridRow` — 칸 단위로 읽는 표(수치 · 체크 · 입력). 칸마다 1px 선
 * 칸 수 · 너비가 다른 도메인 표는 이 맵으로 줄 · 칸을 직접 조립한다(`TableRow` · `TableCell` · `TableGridRow` · `TableGridCell`).
 */

export const TABLE_ROW_TYPES = ['header', 'row'] as const;
/** Figma `type`. header = 머리 줄, row = 본문 줄 */
export type TableRowType = (typeof TABLE_ROW_TYPES)[number];

export const TABLE_DENSITIES = ['default', 'compact', 'dense'] as const;
/** Figma `_Table/Row` 의 `density`(default 54 · compact 43 · dense 35) */
export type TableDensity = (typeof TABLE_DENSITIES)[number];

export const TABLE_GRID_DENSITIES = ['compact', 'dense'] as const;
/** Figma `_Table/GridRow` 의 `density`(compact 40 · dense 28) */
export type TableGridDensity = (typeof TABLE_GRID_DENSITIES)[number];

export const TABLE_STATUSES = ['default', 'init', 'empty', 'loading'] as const;
export type TableStatus = (typeof TABLE_STATUSES)[number];

/** 칸 안의 정렬 */
export const TABLE_CELL_ALIGNS = ['start', 'center', 'end'] as const;
export type TableCellAlign = (typeof TABLE_CELL_ALIGNS)[number];

/**
 * 칸 너비. fill = 남은 폭을 나눠 가짐(기본 — Figma 의 FILL), hug = 내용만큼,
 * xs 80 · sm 100 · md 120 · lg 140 · xl 200 · 2xl 300 = 고정 폭(`size.button.*` 단계와 같은 값).
 * 그 밖의 폭은 `className` 으로 준다.
 */
export const TABLE_COLUMN_WIDTHS = ['fill', 'hug', 'xs', 'sm', 'md', 'lg', 'xl', '2xl'] as const;
export type TableColumnWidth = (typeof TABLE_COLUMN_WIDTHS)[number];

/** fill 칸끼리의 비율(flex-grow). 격자 표의 항목 이름 칸 160 : 값 칸 140 = 8 : 7 */
export const TABLE_COLUMN_GROWS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export type TableColumnGrow = (typeof TABLE_COLUMN_GROWS)[number];

// ── 목록 표의 줄 (`_Table/Row`) ─────────────────────────────────────────

/** 줄 공통: 가로 배치, 칸 사이 16, 좌우 여백 16, 흰 면 */
export const TABLE_ROW_BASE_CLASS = 'flex w-full items-center gap-4 bg-bg-surface px-4';

/**
 * 줄의 위아래 여백 · 선 · 글자. [density][type]
 * default 머리 줄은 높이 44 고정(글자 20 + 위 12 + 아래 10 + 선 2), 본문 줄은 위아래 16 + 선 2.
 * compact · dense 머리 줄은 위아래 1px 선, 본문 줄은 아래 1px 선.
 */
export const TABLE_ROW_CLASS: Record<TableDensity, Record<TableRowType, string>> = {
  default: {
    header: 'border-b-2 border-bg-canvas pt-3 pb-2-5 text-fg-default typo-label',
    row: 'border-b-2 border-bg-canvas py-4 text-fg-default typo-body-sm',
  },
  compact: {
    header: 'border-y border-border-default py-3 text-fg-default typo-label-sm',
    row: 'border-b border-border-default py-3 text-fg-default typo-caption',
  },
  dense: {
    header: 'border-y border-border-default py-2 text-fg-default typo-label-sm',
    row: 'border-b border-border-default py-2 text-fg-default typo-caption',
  },
};

/** 칸 공통: 내용이 넘치면 칸 안에서 자른다(Figma clip) */
export const TABLE_CELL_BASE_CLASS = 'flex min-w-0 items-center';

export const TABLE_COLUMN_WIDTH_CLASS: Record<TableColumnWidth, string> = {
  fill: 'basis-0',
  hug: 'shrink-0',
  xs: 'w-(--size-button-xs) shrink-0',
  sm: 'w-(--size-button-sm) shrink-0',
  md: 'w-(--size-button-md) shrink-0',
  lg: 'w-(--size-button-lg) shrink-0',
  xl: 'w-(--size-button-xl) shrink-0',
  '2xl': 'w-(--size-button-2xl) shrink-0',
};

/** fill 칸의 비율. fill 이 아닌 칸에는 붙이지 않는다 */
export const TABLE_COLUMN_GROW_CLASS: Record<TableColumnGrow, string> = {
  1: 'grow',
  2: 'grow-2',
  3: 'grow-3',
  4: 'grow-4',
  5: 'grow-5',
  6: 'grow-6',
  7: 'grow-7',
  8: 'grow-8',
};

export const TABLE_CELL_ALIGN_CLASS: Record<TableCellAlign, string> = {
  start: 'justify-start text-left',
  center: 'justify-center text-center',
  end: 'justify-end text-right',
};

/** 고르기 칸(Checkbox md 16) */
export const TABLE_SELECT_CELL_CLASS = 'flex shrink-0 items-center';

// ── 격자 표의 줄 (`_Table/GridRow`) ─────────────────────────────────────

/** 격자 줄: 칸이 딱 붙고(간격 0) 선은 칸이 그린다 */
export const TABLE_GRID_ROW_BASE_CLASS = 'flex w-full bg-bg-surface';

/** 격자 칸의 높이. compact 40 · dense 28 */
export const TABLE_GRID_DENSITY_CLASS: Record<TableGridDensity, string> = {
  compact: 'h-(--size-control-md)',
  dense: 'h-(--size-control-xs)',
};

/** 격자 칸 공통: 좌우 12 · 위아래 4, 넘치면 자른다 */
export const TABLE_GRID_CELL_BASE_CLASS =
  'flex min-w-0 items-center overflow-hidden border-border-subtle px-3 py-1';

/**
 * 격자 칸의 면 · 글자 · 선. [type][칸 종류]
 * label = 첫 칸(항목 이름). 머리 줄은 옅은 primary 면 + label-sm, 본문 줄의 첫 칸은 옅은 회색 면, 본문은 caption.
 * 선은 칸마다 1px — 첫 칸만 왼쪽 선을, 머리 줄만 위 선을 긋는다(겹치지 않게).
 */
export const TABLE_GRID_CELL_CLASS: Record<TableRowType, Record<'label' | 'value', string>> = {
  header: {
    label: 'border bg-bg-primary-subtle text-fg-default typo-label-sm',
    value: 'border-y border-r bg-bg-primary-subtle text-fg-default typo-label-sm',
  },
  row: {
    label: 'border-x border-b bg-bg-subtle text-fg-default typo-caption',
    value: 'border-r border-b bg-bg-surface text-fg-default typo-caption',
  },
};

// ── 한 칸에 여러 줄 (`_Table/StackCell`) ────────────────────────────────

export const TABLE_STACK_CELL_CLASS = 'flex min-w-0 flex-col';

/** 줄 하나: 위아래 8(높이 36). 둘째 줄부터 위에 2px 틈(배경색 선) */
export const TABLE_STACK_LINE_CLASS = {
  first: 'flex items-center overflow-hidden py-2 text-fg-default typo-body-sm',
  next: 'flex items-center overflow-hidden border-t-2 border-bg-canvas py-2 text-fg-default typo-body-sm',
} as const;

// ── 표 전체 (`Table`) ───────────────────────────────────────────────────

/** 흰 둥근 틀(모서리 12, shadow-card) */
export const TABLE_FRAME_CLASS =
  'relative flex w-full flex-col overflow-hidden rounded-2xl bg-bg-surface shadow-card';

/** 쪽 이동 자리: 오른쪽 정렬, 좌우 16 · 위아래 12 */
export const TABLE_FOOTER_CLASS = 'flex justify-end px-4 py-3';

/**
 * status=init 의 스켈레톤 줄: 위아래 20 + 막대 12 + 아래 1px 선(Figma 는 선을 안쪽에 그려 52, 코드는 53).
 * 막대 너비는 칸마다 돌아가며 쓴다
 */
export const TABLE_SKELETON_ROW_CLASS =
  'flex w-full items-center gap-4 border-b border-border-default px-4 py-5';

export const TABLE_SKELETON_BAR_CLASS: readonly string[] = [
  'w-(--size-skeleton-80)',
  'w-(--size-skeleton-100)',
  'w-(--size-skeleton-120)',
];

// ── 표 hover(DD-26) ───────────────────────────────────────────────────

/**
 * 표 hover 의 표지(data 속성). 줄 · 묶음 단위로 칸 면을 바꾼다(`shared/design-system/table-hover.css`).
 * - row: `<tr>` — 병합되지 않은 칸에 올리거나 포커스하면 그 줄의 칸이 모두 바뀐다
 * - group: `<tbody>` — 병합 칸에 올리거나 포커스하면 묶음(병합 칸 + 묶음의 모든 줄)이 바뀐다
 * - merged: 병합 칸(`rowSpan`) `<td>` — 줄이 아니라 묶음을 켠다
 */
export const TABLE_HOVER_ATTR = {
  row: { 'data-hover-row': '' },
  group: { 'data-hover-group': '' },
  merged: { 'data-hover-merged': '' },
} as const;

/**
 * 표 hover 면(DD-26): 모든 표가 같은 색 하나 = `bg.row-hover`(primary-50 #edfbfa — 사용자: "primary 이되 더 옅게").
 * 격자 표 머리 면(`bg.primary-subtle`)과 값이 같지만 hover 는 본문 줄에만 생기고 머리 줄과는 칸 선 · 글자로 나뉜다.
 * 칸(td · th)마다 붙인다 — sticky 칸도 같이 바뀐다.
 * row = 줄 hover 만, group = 묶음 hover 만(병합 칸), rowAndGroup = 둘 다(병합 표의 보통 칸)
 */
export const TABLE_HOVER_CELL_CLASS = {
  row: 'row-hover:bg-bg-row-hover',
  group: 'rowgroup-hover:bg-bg-row-hover',
  rowAndGroup: 'row-hover:bg-bg-row-hover rowgroup-hover:bg-bg-row-hover',
} as const;
